import React, { useState, useEffect, useCallback } from 'react';
import {
    MdClock, MdCalendarMonth, MdPerson, MdCheckCircle,
    MdCancel, MdEventAvailable, MdEventBusy, MdKeyboardArrowDown,
    MdWarning, MdInfo
} from 'react-icons/md';
import { usersAPI, workScheduleAPI, api } from 'services/api';
import {BsClock} from "react-icons/bs";

// --- SKELETON LOADERS ---
const SelectSkeleton = () => (
    <div className="flex flex-col gap-2 animate-pulse">
        <div className="h-4 w-24 bg-gray-200 dark:bg-navy-700 rounded-md" />
        <div className="h-12 w-full bg-gray-200 dark:bg-navy-700 rounded-xl" />
    </div>
);

const SlotsSkeleton = () => (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 animate-pulse">
        {[...Array(12)].map((_, i) => (
            <div key={i} className="h-32 w-full bg-gray-100 dark:bg-navy-900/50 rounded-2xl" />
        ))}
    </div>
);

const StaffAvailability = () => {
    // --- STATE (WORK-LOGIC BİREBİR KORUNDU) ---
    const [staffList, setStaffList] = useState([]);
    const [selectedStaff, setSelectedStaff] = useState('');
    const [selectedDate, setSelectedDate] = useState('');

    const [slots, setSlots] = useState([]);
    const [appointments, setAppointments] = useState([]);

    // Yükleme & Hata Stateleri
    const [loadingStaff, setLoadingStaff] = useState(true);
    const [loadingData, setLoadingData] = useState(false);
    const [togglingSlotId, setTogglingSlotId] = useState(null); // Buton bazlı loader
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 24; // Optimizasyon: Sayfada 24 slot gösterilecek (Grid uyumlu)

    // Tarih Sınırları (Orijinal Mantık)
    const today = new Date().toISOString().split('T')[0];
    const maxDateObj = new Date();
    maxDateObj.setDate(maxDateObj.getDate() + 60);
    const maxDate = maxDateObj.toISOString().split('T')[0];

    // --- 1. PERSONEL LİSTESİNİ ÇEK ---
    useEffect(() => {
        const fetchStaff = async () => {
            try {
                setLoadingStaff(true);
                const response = await usersAPI.getStaff();
                setStaffList(response.staff || []);
            } catch (error) {
                console.error('Personel listesi hatası:', error);
                setError('Personel listesi yüklenemedi.');
            } finally {
                setLoadingStaff(false);
            }
        };
        fetchStaff();
    }, []);

    // --- 2. SEÇİME GÖRE SLOTLARI VE RANDEVULARI ÇEK ---
    useEffect(() => {
        if (!selectedStaff || !selectedDate) {
            setSlots([]);
            setAppointments([]);
            setCurrentPage(1);
            return;
        }

        const fetchData = async () => {
            try {
                setLoadingData(true);
                setError(null);
                setSuccess(null);
                setCurrentPage(1); // Yeni veri gelince 1. sayfaya dön

                const [slotsResponse, appointmentsResponse] = await Promise.all([
                    api.get(`/staff/${selectedStaff}/available-slots?start_date=${selectedDate}`),
                    api.get(`/staff/${selectedStaff}/appointments`)
                ]);

                const slotsData = slotsResponse.data || slotsResponse;
                const appointmentsData = appointmentsResponse.data || appointmentsResponse;

                const allSlotsGrouped = slotsData.available_slots || {};
                const daysSlots = allSlotsGrouped[selectedDate] || [];
                setSlots(daysSlots);

                if (appointmentsData.success && appointmentsData.appointments) {
                    setAppointments(appointmentsData.appointments);
                } else {
                    setAppointments([]);
                }

            } catch (error) {
                console.error('Veri çekme hatası:', error);
                setError('Veriler alınırken bir sorun oluştu.');
                setSlots([]);
            } finally {
                setLoadingData(false);
            }
        };

        fetchData();
    }, [selectedStaff, selectedDate]);

    // --- YARDIMCI FONKSİYONLAR (WORK-LOGIC BİREBİR) ---
    const isSlotPast = (dateStr, timeStr) => {
        const now = new Date();
        const slotDateTime = new Date(`${dateStr}T${timeStr}`);
        return slotDateTime < now;
    };

    const findAppointmentForSlot = (slot) => {
        if (!appointments || appointments.length === 0) return null;
        return appointments.find((app) => {
            const isSameDate = app.appointment_date === slot.work_date;
            const appTime = app.appointment_time.substring(0, 5);
            const slotTime = slot.start_time.substring(0, 5);
            const isActive = ['pending', 'approved'].includes(app.status);
            return isSameDate && appTime === slotTime && isActive;
        });
    };

    // --- SLOT AÇMA/KAPAMA İŞLEMİ (OPTIMISTIC UI) ---
    const handleToggleAvailability = useCallback(
        async (slotId, rawIndex) => {
            setTogglingSlotId(slotId);
            setError(null);

            // Orijinal kopyayı yedekle
            const oldSlots = [...slots];
            const updatedSlots = [...slots];

            // Anında UI güncellemesi (Optimistic Update)
            updatedSlots[rawIndex] = {
                ...updatedSlots[rawIndex],
                is_available: !updatedSlots[rawIndex].is_available
            };
            setSlots(updatedSlots);

            try {
                const response = await workScheduleAPI.toggleSlot(slotId);
                if (response && !response.success && !response.work_hour) {
                    throw new Error('API Success false döndü');
                }
            } catch (error) {
                console.error('Slot güncelleme hatası:', error);
                setSlots(oldSlots); // Hata olursa eski haline döndür
                setError('Slot güncellenemedi. Lütfen bağlantınızı kontrol edin.');
            } finally {
                setTogglingSlotId(null);
            }
        },
        [slots]
    );

    // --- SAYFALAMA (PAGINATION) HESAPLAMALARI ---
    // rawIndex'i kaybetmemek için map atıyoruz
    const mappedSlots = slots.map((slot, index) => ({ ...slot, rawIndex: index }));
    const totalPages = Math.max(1, Math.ceil(mappedSlots.length / PAGE_SIZE));
    const currentSlots = mappedSlots.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);


    // --- RENDER ---
    return (
        <div className="mt-5 flex w-full flex-col gap-6 pb-10 animate-fadeIn">

            {/* 1. ÜST BAŞLIK ALANI */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-white p-6 rounded-[20px] shadow-3xl shadow-shadow-500 dark:bg-navy-800 dark:shadow-none border border-gray-100 dark:border-white/10">
                <div className="flex items-center gap-4 w-full md:w-auto">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-500/10 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400">
                        <BsClock className="text-3xl" />
                    </div>
                    <div>
                        <h4 className="text-xl font-bold text-navy-700 dark:text-white">Müsaitlik Yönetimi</h4>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Personel takvimi üzerinden randevu slotlarını açın veya kapatın.
                        </p>
                    </div>
                </div>
            </div>

            {/* BİLDİRİMLER */}
            {(error || success) && (
                <div className={`flex items-center gap-3 p-4 rounded-[16px] border ${error ? 'bg-red-50 border-red-200 text-red-600 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400' : 'bg-green-50 border-green-200 text-green-600 dark:bg-green-500/10 dark:border-green-500/20 dark:text-green-400'}`}>
                    <MdInfo className="text-2xl flex-none" />
                    <span className="text-sm font-bold">{error || success}</span>
                </div>
            )}

            {/* 2. FİLTRE & SEÇİM KARTI */}
            <div className="bg-white p-6 rounded-[20px] shadow-sm border border-gray-100 dark:bg-navy-800 dark:border-white/10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                    {/* Personel Seçimi */}
                    {loadingStaff ? (
                        <SelectSkeleton />
                    ) : (
                        <div>
                            <label className="text-sm font-bold text-navy-700 dark:text-white ml-1 mb-2 flex items-center gap-2">
                                <MdPerson className="text-brand-500" /> Personel Seçin
                            </label>
                            <div className="relative">
                                <select
                                    value={selectedStaff}
                                    onChange={(e) => setSelectedStaff(e.target.value)}
                                    className="w-full h-12 appearance-none rounded-xl border border-gray-200 bg-gray-50 px-4 text-sm font-bold text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white transition-all cursor-pointer"
                                >
                                    <option value="">Lütfen listeden seçiniz...</option>
                                    {staffList.map((staff) => (
                                        <option key={staff.id} value={staff.id}>
                                            {staff.name} {staff.surname}
                                        </option>
                                    ))}
                                </select>
                                <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-white">
                                    <MdKeyboardArrowDown className="text-xl" />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Tarih Seçimi */}
                    <div>
                        <label className="text-sm font-bold text-navy-700 dark:text-white ml-1 mb-2 flex items-center gap-2">
                            <MdCalendarMonth className="text-amber-500" /> Tarih Seçin
                        </label>
                        <div className="relative">
                            <input
                                type="date"
                                value={selectedDate}
                                min={today}
                                max={maxDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                disabled={!selectedStaff}
                                className="w-full h-12 rounded-xl border border-gray-200 bg-gray-50 px-4 text-sm font-bold text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            />
                        </div>
                    </div>

                </div>
            </div>

            {/* 3. SLOTLAR (İÇERİK) */}
            <div className="bg-white p-6 rounded-[20px] shadow-sm border border-gray-100 dark:bg-navy-800 dark:border-white/10 min-h-[400px]">
                {loadingData ? (
                    <SlotsSkeleton />
                ) : !selectedStaff || !selectedDate ? (
                    /* BOŞ DURUM - SEÇİM BEKLENİYOR */
                    <div className="flex flex-col items-center justify-center py-16 opacity-60">
                        <MdEventAvailable className="text-7xl text-gray-300 dark:text-gray-600 mb-4" />
                        <h5 className="text-xl font-bold text-navy-700 dark:text-white">Seçim Bekleniyor</h5>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mt-2 text-center max-w-sm">
                            Randevu slotlarını görüntülemek ve yönetmek için lütfen yukarıdan personel ve tarih seçimi yapın.
                        </p>
                    </div>
                ) : slots.length === 0 ? (
                    /* BOŞ DURUM - SLOT YOK */
                    <div className="flex flex-col items-center justify-center py-16">
                        <div className="h-20 w-20 rounded-full bg-red-50 flex items-center justify-center dark:bg-red-500/10 mb-4">
                            <MdEventBusy className="text-4xl text-red-500 dark:text-red-400" />
                        </div>
                        <h5 className="text-xl font-bold text-navy-700 dark:text-white">Kayıt Bulunamadı</h5>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mt-2 text-center max-w-md">
                            Seçilen personelin bu tarihte çalışma kaydı bulunmuyor. Lütfen "Çalışma Planlayıcı" sayfasından şablon oluşturun.
                        </p>
                    </div>
                ) : (
                    /* SLOT GRID MİMARİSİ */
                    <>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                            {currentSlots.map((slot) => {
                                const isPast = isSlotPast(slot.work_date, slot.end_time);
                                const appointment = !slot.is_available ? findAppointmentForSlot(slot) : null;
                                const isToggling = togglingSlotId === slot.id;

                                // HorizonUI Slot Dinamikleri
                                let baseCardClass = "relative flex flex-col items-center justify-between p-4 rounded-2xl border-2 transition-all duration-200 overflow-hidden ";
                                let bgClass = "";
                                let borderClass = "";
                                let textClass = "text-navy-700 dark:text-white";
                                let statusText = "";
                                let statusColor = "";

                                if (isPast) {
                                    // GEÇMİŞ ZAMAN (Gri & Pasif)
                                    bgClass = "bg-gray-100 dark:bg-navy-900";
                                    borderClass = "border-transparent";
                                    textClass = "text-gray-400 dark:text-gray-500";
                                    statusText = "SÜRE DOLDU";
                                    statusColor = "text-gray-400";
                                } else if (appointment) {
                                    // DOLU RANDEVU (Lüks Gradient)
                                    bgClass = "bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-lg shadow-brand-500/30";
                                    borderClass = "border-transparent";
                                    textClass = "text-white";
                                    statusText = "DOLU";
                                    statusColor = "text-white/80";
                                } else if (slot.is_available) {
                                    // AÇIK & MÜSAİT (Yeşil Tonlar)
                                    bgClass = "bg-green-50 dark:bg-green-500/10 hover:shadow-md";
                                    borderClass = "border-green-500 dark:border-green-400";
                                    statusText = "MÜSAİT";
                                    statusColor = "text-green-600 dark:text-green-400";
                                } else {
                                    // KAPALI (Kırmızı Tonlar)
                                    bgClass = "bg-red-50 dark:bg-red-500/10 hover:shadow-md opacity-90";
                                    borderClass = "border-red-500 dark:border-red-400";
                                    statusText = "KAPALI";
                                    statusColor = "text-red-600 dark:text-red-400";
                                }

                                return (
                                    <div key={slot.id} className={`${baseCardClass} ${bgClass} ${borderClass}`}>

                                        {/* Saat Görüntüsü */}
                                        <div className={`text-xl font-black font-mono tracking-wider ${textClass}`}>
                                            {slot.start_time.substring(0, 5)}
                                        </div>

                                        {/* Durum / Randevu Detayı */}
                                        <div className="flex flex-col items-center justify-center my-3 w-full min-h-[40px]">
                                            {appointment ? (
                                                <div className="flex flex-col items-center w-full animate-fadeIn">
                          <span className="text-[11px] font-bold text-white text-center truncate w-full px-1">
                            {appointment.customer?.name} {appointment.customer?.surname?.charAt(0)}.
                          </span>
                                                    <span className="mt-1 px-2 py-0.5 rounded bg-white/20 text-[9px] font-bold text-white uppercase">
                            {appointment.status === 'approved' ? 'Onaylı' : 'Bekliyor'}
                          </span>
                                                </div>
                                            ) : (
                                                <span className={`text-[10px] font-black tracking-widest ${statusColor}`}>
                          {statusText}
                        </span>
                                            )}
                                        </div>

                                        {/* Aksiyon Butonu */}
                                        {!isPast && !appointment && (
                                            <button
                                                disabled={isToggling}
                                                onClick={() => handleToggleAvailability(slot.id, slot.rawIndex)}
                                                className={`w-full py-1.5 rounded-lg text-xs font-bold transition-all flex items-center justify-center disabled:opacity-50 ${
                                                    slot.is_available
                                                        ? 'bg-red-100 text-red-600 hover:bg-red-500 hover:text-white dark:bg-red-500/20 dark:text-red-400'
                                                        : 'bg-green-100 text-green-600 hover:bg-green-500 hover:text-white dark:bg-green-500/20 dark:text-green-400'
                                                }`}
                                            >
                                                {isToggling ? (
                                                    <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                                ) : (
                                                    slot.is_available ? 'KAPAT' : 'AÇ'
                                                )}
                                            </button>
                                        )}

                                        {/* Engellenmiş Boşluk (Past & Booked durumları formata uysun diye) */}
                                        {(isPast || appointment) && <div className="h-7 w-full" />}
                                    </div>
                                );
                            })}
                        </div>

                        {/* SAYFALAMA (PAGINATION) - Kural gereği listeleme optimizasyonu */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100 dark:border-white/10">
                                <p className="text-sm font-medium text-gray-500">
                                    Toplam <span className="font-bold text-navy-700 dark:text-white">{slots.length}</span> slot. Sayfa: {currentPage}/{totalPages}
                                </p>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        className="px-4 py-2 rounded-xl border border-gray-200 bg-white text-sm font-bold text-navy-700 disabled:opacity-40 hover:bg-gray-50 transition-all dark:border-white/10 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
                                    >
                                        Önceki
                                    </button>
                                    <button
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                        className="px-4 py-2 rounded-xl border border-gray-200 bg-white text-sm font-bold text-navy-700 disabled:opacity-40 hover:bg-gray-50 transition-all dark:border-white/10 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
                                    >
                                        Sonraki
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

        </div>
    );
};

export default StaffAvailability;